import { IconButton, Spacer, Stack, Tooltip, useColorMode } from '@chakra-ui/react';
import { getTenantByHost, isCrudRouteEnabledForTenant } from '@pelicargo/tenants';
import { Show, useError } from '@pelicargo/ui';
import { AirlineIcon } from '@pelicargo/ui';
import { AppShell, Sidebar, SidebarSection } from '@saas-ui/react';
import { ReactElement, ReactNode, useMemo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  HiOutlineAdjustmentsHorizontal,
  HiOutlineClock,
  HiOutlineInboxArrowDown,
  HiOutlineQuestionMarkCircle,
  HiOutlineSparkles,
  HiOutlineWrenchScrewdriver,
  HiVariable,
} from 'react-icons/hi2';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { trpc } from '../config/trpc';
import { useAuth } from '../context/AuthProvider';
import { useDashboard } from '../hooks/useDashboard';
import { getCmdCtrlLabel } from '../utils/platform-hot-key';
import { AccountsIcon } from './Icons/AccountsIcon';
import { AirportIcon } from './Icons/AirportIcon';
import { ContactsIcon } from './Icons/ContactsIcon';
import { DashboardIcon } from './Icons/DashboardIcon';
import { ImporterIcon } from './Icons/ImporterIcon';
import { InstructionsIcon } from './Icons/InstructionsIcon';
import { LogoutIcon } from './Icons/LogoutIcon';
import { MoonIcon } from './Icons/MoonIcon';
import { RouteIcon } from './Icons/RouteIcon';
import { SunIcon } from './Icons/SunIcon';
import { SurchargesIcon } from './Icons/SurchargesIcon';
import { UsersIcon } from './Icons/UsersIcon';

type Props = {
  to?: string;
  isActive: boolean;
  label: string;
  icon: ReactElement;
  onClick?: () => void;
  checkTenant?: boolean;
  newTab?: boolean;
};

const NavItem = ({ to, isActive, label, icon, onClick, checkTenant = true, newTab }: Props) => {
  if (!to) {
    return (
      <Tooltip label={label}>
        <IconButton
          aria-label={label}
          variant={isActive ? 'solid' : 'ghost'}
          colorScheme={isActive ? 'primary' : 'gray'}
          icon={icon}
          onClick={onClick}
        />
      </Tooltip>
    );
  }

  const isVisible = isCrudRouteEnabledForTenant(to ?? '');
  if (!isVisible && checkTenant) return null;

  return (
    <Tooltip label={label}>
      <Link to={to} target={newTab ? '_blank' : undefined}>
        <IconButton
          aria-label={label}
          variant={isActive ? 'solid' : 'ghost'}
          colorScheme={isActive ? 'primary' : 'gray'}
          icon={icon}
        />
      </Link>
    </Tooltip>
  );
};

const DashboardSidebar = () => {
  const { logout } = useAuth();
  const { handleError } = useError();
  const location = useLocation();
  const dashboard = useDashboard();
  const navigate = useNavigate();

  const createStudioSession = trpc.createStudioSession.useMutation();

  const openStudio = async () => {
    try {
      const { url } = await createStudioSession.mutateAsync();
      window.open(url, '_blank');
    } catch (error) {
      handleError(error);
    }
  };

  const { toggleColorMode, colorMode } = useColorMode();
  const isDarkMode = colorMode === 'dark';

  const openCalculator = () => {
    window.open('/calculator', undefined, 'popup=true,width=800,height=600');
  };

  const handleToggle = () => {
    const isOnRequestPage = location.pathname.includes('/requests');
    if (isOnRequestPage) {
      dashboard.onToggle();
      return;
    }

    navigate('/requests');
  };

  useHotkeys(['alt+z', 'mod+k'], openCalculator, {
    preventDefault: true,
  });
  const canSeeRateManagement = useMemo(() => {
    return (
      isCrudRouteEnabledForTenant('/surcharges') ||
      isCrudRouteEnabledForTenant('/rates') ||
      isCrudRouteEnabledForTenant('/route-editor') ||
      isCrudRouteEnabledForTenant('/markup-configurations')
    );
  }, []);

  return (
    <>
      <Sidebar variant="compact">
        <SidebarSection>
          <NavItem
            label="Requests"
            onClick={handleToggle}
            icon={<DashboardIcon />}
            isActive={location.pathname.includes('/requests')}
          />
          <NavItem
            to="/ai-inbox"
            label="AI Inbox"
            isActive={location.pathname === '/ai-inbox'}
            icon={<HiOutlineInboxArrowDown size={16} />}
          />
        </SidebarSection>
        <Spacer />
        <SidebarSection title="Data Management" borderBottomWidth={1} pb="2">
          <Stack>
            <NavItem
              to="/accounts"
              label="Accounts"
              isActive={location.pathname === '/accounts'}
              icon={<AccountsIcon />}
            />
            <NavItem
              to="/providers"
              label="Providers"
              isActive={location.pathname === '/providers'}
              icon={<AirlineIcon />}
            />
            <NavItem
              to="/airports"
              label="Airports"
              isActive={location.pathname === '/airports'}
              icon={<AirportIcon />}
            />
            <NavItem
              to="/contacts"
              label="Contacts"
              isActive={location.pathname === '/contacts'}
              icon={<ContactsIcon />}
            />
            <NavItem
              to="/instructions"
              label="Instructions"
              isActive={location.pathname === '/instructions'}
              icon={<InstructionsIcon />}
            />

            <NavItem to="/users" label="Users" isActive={location.pathname === '/users'} icon={<UsersIcon />} />

            <NavItem
              icon={<HiOutlineClock size={16} />}
              to="/backlogs"
              label="Backlogs"
              isActive={location.pathname === '/backlogs'}
            />
          </Stack>
        </SidebarSection>
        <SidebarSection
          title="Rate Management"
          borderBottomWidth={1}
          pb="2"
          display={canSeeRateManagement ? undefined : 'none'}
        >
          <Stack>
            <NavItem
              to="/surcharges"
              label="Surcharges"
              isActive={location.pathname === '/surcharges'}
              icon={<SurchargesIcon />}
            />
            <NavItem
              to="/markup-configurations"
              label="Markup Configurations"
              isActive={location.pathname === '/markup-configurations'}
              icon={<HiOutlineAdjustmentsHorizontal size={16} />}
            />
            <NavItem
              to="/rates"
              isActive={location.pathname.includes('rates')}
              label="Rate Importer"
              icon={<ImporterIcon />}
            />
            <NavItem
              to="/route-editor"
              label="Route Editor"
              isActive={location.pathname === '/route-editor'}
              icon={<RouteIcon />}
            />
          </Stack>
        </SidebarSection>
        <SidebarSection>
          <Show if={dashboard?.isDevMode}>
            <NavItem
              to="/configuration"
              label="Advanced Configuration"
              isActive={location.pathname.startsWith('/configuration')}
              icon={<HiOutlineWrenchScrewdriver size={16} />}
            />
          </Show>
          <Show if={dashboard?.isDevMode && getTenantByHost().isSystemTenant}>
            <NavItem
              label="Mastra Studio"
              isActive={false}
              icon={<HiOutlineSparkles size={16} />}
              onClick={openStudio}
            />
          </Show>
          <NavItem
            icon={<HiVariable size="18" />}
            label={`Calculator (${getCmdCtrlLabel('K')})`}
            isActive={false}
            onClick={openCalculator}
          />
          <NavItem
            icon={isDarkMode ? <SunIcon /> : <MoonIcon />}
            label="Toggle Color Mode"
            isActive={false}
            onClick={toggleColorMode}
          />
          <Show if={getTenantByHost()['helpUrl']}>
            <NavItem
              icon={<HiOutlineQuestionMarkCircle size="18" />}
              label={`Help`}
              isActive={false}
              to={getTenantByHost()['helpUrl']}
              checkTenant={false}
              newTab
            />
          </Show>
          <NavItem icon={<LogoutIcon />} label="Log Out" isActive={false} onClick={logout} />
        </SidebarSection>
      </Sidebar>
    </>
  );
};

type DashboardProps = {
  children: ReactNode;
};

export const Dashboard = ({ children }: DashboardProps) => {
  return <AppShell sidebar={<DashboardSidebar />}>{children}</AppShell>;
};
